import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement } from 'lit/decorators.js';
import componentStyles from './tufts-hero-with-icon-links.css.lit';
import '../tufts-card/tufts-card';
import tuftsHeadingCssLit from '../tufts-heading/tufts-heading.css.lit';
import { ResizeController } from '../../controllers/resize-controller';
import tuftsSearchBarDynamicStyles from '../tufts-search-bar-dynamic/tufts-search-bar-dynamic.css.lit';
import '../tufts-container/tufts-container';
import { SlotNGController } from '../../controllers/slot-ng-controller';

/**
 * The tuftsHeroWithIconLinks component
 * @element tufts-hero-with-icon-links
 *
 * @slot title
 * @slot body
 * @slot search
 * @slot icon-link
 * @slot image
 */
@customElement('tufts-hero-with-icon-links')
export class tuftsHeroWithIconLinks extends OutlineElement {
  ngSlot = new SlotNGController(this);
  resizeController: ResizeController = new ResizeController(this, {
    breakpoints: [860, 1960],
  });
  static styles: CSSResultGroup = [
    componentStyles,
    tuftsHeadingCssLit,
    // added the styles of the tufts-search-bar-dynamic so you don't lose the styles
    tuftsSearchBarDynamicStyles,
  ];
  randomIndex: number;
  randomImage: HTMLElement;

  connectedCallback() {
    super.connectedCallback();
    const imageArray = this.querySelectorAll('[slot="image"]');
    // Generate a random index to select a random image URL from the array
    this.randomIndex = Math.floor(Math.random() * imageArray.length);
    this.randomImage = imageArray[this.randomIndex].cloneNode(
      true
    ) as HTMLElement;
    // Unhide image
    this.randomImage.style.display = 'inherit';
    this.checkParentNodeContainer();
  }
  /**
   * Checks for a parent tufts-container element and sets its padding to zero
   * to ensure proper spacing for the hero component
   */
  checkParentNodeContainer() {
    // get the parent node component
    const parentElement = this.closest('tufts-container') as Element;
    if (parentElement) {
      parentElement.setAttribute('padding', 'space-0');
    }
  }

  mediumScreen(): TemplateResult {
    return html`
      <div class="medium">
        <tufts-container
          pulse-orientation="bottom-right"
          max-pulse-size="45%"
          pulse-mobile="true"
          container-width="full"
          padding="0"
        >
          <tufts-container
            container-width="wide"
            padding="0"
            background-color="transparent"
          >
            <div class="main-wrapper desktop">
              <div class="content">
                ${this.ngSlot.conditionalNGSlot('title', true, 'xxl')}
                <div class="sub-content">
                  ${this.ngSlot.conditionalNGSlot(
                    'body',
                    true,
                    'md special-heading'
                  )}
                  ${this.ngSlot.conditionalNGSlot('search', false)}
                  ${this.ngSlot.conditionalNGSlot('icon-link')}
                </div>
              </div>
              <div class="media">${this.randomImage}</div>
            </div>
          </tufts-container>
        </tufts-container>
      </div>
    `;
  }

  wideScreen(): TemplateResult {
    return html`
      <tufts-container container-width="wide" padding="0">
        <tufts-container
          pulse-orientation="bottom-right"
          max-pulse-size="50%"
          container-width="full"
          pulse-mobile="true"
          padding="0"
          background-color="transparent"
        >
          <div class="main-wrapper desktop wide">
            <div class="content">
              ${this.ngSlot.conditionalNGSlot('title', true, 'xxl')}
              <div class="sub-content">
                ${this.ngSlot.conditionalNGSlot(
                  'body',
                  true,
                  'md special-heading'
                )}
                ${this.ngSlot.conditionalNGSlot('search', false)}
                ${this.ngSlot.conditionalNGSlot('icon-link')}
              </div>
            </div>
            <div class="media">${this.randomImage}</div>
          </div>
        </tufts-container>
      </tufts-container>
    `;
  }

  smallScreen(): TemplateResult {
    return html`
      <div class="small-main-wrapper small">
        ${this.ngSlot.conditionalNGSlot('title', true, 'xxl')}
        <tufts-container
          pulse-orientation="bottom-right"
          container-width="full"
          pulse-mobile="true"
          padding="0"
          class="wrapper"
        >
          <div class="small-container">${this.randomImage}</div>
        </tufts-container>
        ${this.ngSlot.conditionalNGSlot('body', true, 'md special-heading')}
        ${this.ngSlot.conditionalNGSlot('search', false)}
        ${this.ngSlot.conditionalNGSlot('icon-link')}
      </div>
    `;
  }

  render(): TemplateResult {
    const range = this.resizeController.currentBreakpointRange;
    switch (range) {
      case 0:
        return this.smallScreen();
      case 1:
        return this.mediumScreen();
      default:
        return this.wideScreen();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-hero-with-icon-links': tuftsHeroWithIconLinks;
  }
}
